import { useEffect } from 'react';
import { X, Bell, AlertTriangle, Megaphone, Info } from 'lucide-react';
import clsx from 'clsx';
import { useNotifications } from '@/store/notifications';
import { useNotificationsFeed } from '@/lib/useNotificationsFeed';
import type { NotificationItem, NotificationType } from '@/lib/api';

/**
 * Sağdan açılan bildirim çekmecesi. Eski projedeki offsidebar bildirim
 * panelinin karşılığı. Topbar bell tıklayınca açılır; açılınca okunmamışlar
 * "görüldü" olarak işaretlenir (badge sıfırlanır).
 */
export function NotificationDrawer() {
  const open = useNotifications((s) => s.drawerOpen);
  const close = useNotifications((s) => s.closeDrawer);
  const markSeen = useNotifications((s) => s.markSeen);
  const { items, maxId, isLoading } = useNotificationsFeed();

  // Çekmece açıkken en yüksek id'yi görüldü işaretle (badge sıfırlanır).
  useEffect(() => {
    if (open && maxId > 0) markSeen(maxId);
  }, [open, maxId, markSeen]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, close]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-[1px]" onClick={close} />
      {/* Panel */}
      <aside className="fixed top-0 right-0 z-[61] h-full w-[360px] max-w-[90vw] bg-surface shadow-2xl flex flex-col animate-[slideIn_.18s_ease-out]">
        <header className="flex items-center gap-2 px-4 h-14 border-b border-slate-100 flex-shrink-0">
          <span className="w-7 h-7 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center">
            <Bell size={15} />
          </span>
          <h2 className="text-[14px] font-semibold text-slate-900 flex-1">Bildirimler</h2>
          <button onClick={close} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="py-12 text-center text-[12.5px] text-slate-400">Yükleniyor…</div>
          )}
          {!isLoading && items.length === 0 && (
            <div className="py-16 text-center">
              <Bell size={26} className="text-slate-200 mx-auto mb-2" />
              <p className="text-[12.5px] text-slate-500 font-medium">Henüz bildirim yok</p>
              <p className="text-[11px] text-slate-400 mt-1">Takım lideri duyuruları burada görünecek</p>
            </div>
          )}
          {items.map((n) => <NotificationRow key={n.id} n={n} />)}
        </div>
      </aside>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

const TYPE_META: Record<NotificationType, { Icon: typeof Bell; tone: string; label: string }> = {
  broadcast: { Icon: Megaphone,     tone: 'bg-brand-100 text-brand-700',   label: 'Duyuru' },
  fraud:     { Icon: AlertTriangle, tone: 'bg-red-100 text-red-700',       label: 'Uyarı' },
  system:    { Icon: Info,          tone: 'bg-slate-100 text-slate-600',   label: 'Sistem' },
};

function NotificationRow({ n }: { n: NotificationItem }) {
  const meta = TYPE_META[n.type] ?? TYPE_META.system;
  return (
    <div className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50/60 transition">
      <div className="flex items-start gap-2.5">
        <span className={clsx('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5', meta.tone)}>
          <meta.Icon size={14} />
        </span>
        <div className="min-w-0 flex-1">
          {n.title && <div className="text-[13px] font-semibold text-slate-900 leading-snug">{n.title}</div>}
          <div className="text-[12.5px] text-slate-700 leading-snug whitespace-pre-wrap break-words">{n.body}</div>
          <div className="text-[10.5px] text-slate-400 mt-1 flex items-center gap-1.5">
            <span>{meta.label}</span>
            <span>·</span>
            <span>{fmtRelative(n.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
